/**
 * File: useEffortMutations.ts
 * Path: apps/admin/src/shared/hooks/useEffortMutations.ts
 * Description: Mutation hooks split out of useEffort.ts.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  hourlyRateFor,
  todayInSouthAfrica,
} from "@idk/config";
import { toast } from "@idk/ui";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { useCrmSettings } from "@/shared/hooks/useCrmSettingsQueries";
import {
  effortRepo,
  type NewCost,
  type NewTime,
} from "../services/effortService";
import { effortKey } from "./useEffortQueries";

/** The signed-in person as the one doing the work, with their hourly rate. */
export const useWorker = () => {
  const { user } = useAuth();
  const { settings } = useCrmSettings();
  const email = user?.email ?? "";
  return {
    worker: { uid: user?.uid ?? "", email },
    rateCents: hourlyRateFor(settings, email),
  };
};
const reportError = (action: string) => (error: unknown) => {
  console.error(`[Effort] ${action} failed:`, error);
  toast.error(error instanceof Error ? error.message : `Could not ${action}.`);
};
export const useEffortMutations = () => {
  const queryClient = useQueryClient();
  const { worker, rateCents } = useWorker();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: effortKey });

  return {
    rateCents,
    addTime: useMutation({
      mutationFn: (input: Omit<NewTime, "rateCents" | "date"> & { date?: string }) =>
        effortRepo.addTime({ ...input, date: input.date ?? todayInSouthAfrica(), rateCents }, worker),
      onSuccess: refresh,
      onError: reportError("save the time"),
    }),
    addCost: useMutation({
      mutationFn: (input: Omit<NewCost, "date"> & { date?: string }) =>
        effortRepo.addCost({ ...input, date: input.date ?? todayInSouthAfrica() }, worker),
      onSuccess: refresh,
      onError: reportError("save the cost"),
    }),
    removeTime: useMutation({
      mutationFn: (id: string) => effortRepo.removeTime(id),
      onSuccess: refresh,
      onError: reportError("remove the entry"),
    }),
    removeCost: useMutation({
      mutationFn: (id: string) => effortRepo.removeCost(id),
      onSuccess: refresh,
      onError: reportError("remove the entry"),
    }),
  };
};
